import React, { useEffect, useState } from "react";
import axios from "axios";
import Navbar from "../components/navbar";

const GMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@gmail\.com$/;
const getGmailError = (value = "") =>
    value && !GMAIL_REGEX.test(value.trim())
        ? "Solo se admiten correos de Gmail (@gmail.com)."
        : "";

const OpenVoucherCheckout = () => {
    const params = new URLSearchParams(window.location.search);
    const openVoucherId = params.get("openVoucherId");

    const [voucher, setVoucher] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [couponCode, setCouponCode] = useState("");
    const [coupons, setCoupons] = useState([]);
    const [couponError, setCouponError] = useState("");
    const [isValidCoupon, setIsValidCoupon] = useState(false);
    const [couponId, setCouponId] = useState("");
    const [discount, setDiscount] = useState(0);
    const [emailError, setEmailError] = useState("");
    const [giftEmailError, setGiftEmailError] = useState("");
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        isGift: false,
        giftName: "",
        giftEmail: "",
        message: "",
        vouchersQty: 1, // cantidad de bonos; cada bono es fijo para 2 personas
    });

    useEffect(() => {
        const fetchVoucher = async () => {
            if (!openVoucherId) return;
            setLoading(true);
            setError("");
            try {
                const res = await axios.get(
                    `${process.env.REACT_APP_URI}/v1/open-vouchers/${openVoucherId}`
                );
                setVoucher(res.data);
            } catch (error) {
                console.error("No se pudo cargar el bono", error);
                setError("No pudimos cargar el bono. Verifica el enlace o inténtalo más tarde.");
            } finally {
                setLoading(false);
            }
        };
        fetchVoucher();
    }, [openVoucherId]);

    useEffect(() => {
        const fetchCoupons = async () => {
            try {
                const res = await axios.get(`${process.env.REACT_APP_URI}/v1/coupons`);
                setCoupons(res.data || []);
            } catch (err) {
                console.error("Error obteniendo cupones", err);
            }
        };
        fetchCoupons();
    }, []);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        const nextValue = type === "checkbox" ? checked : value;

        if (name === "email") {
            setEmailError(getGmailError(nextValue));
        }

        if (name === "giftEmail") {
            setGiftEmailError(getGmailError(nextValue));
        }

        setForm((prev) => ({
            ...prev,
            [name]: nextValue,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!voucher?.id) return;
        setLoading(true);
        try {
            const buyerEmail = form.email.trim();
            const giftEmail = form.giftEmail.trim();

            if (!GMAIL_REGEX.test(buyerEmail)) {
                setEmailError("Solo se admiten correos de Gmail (@gmail.com).");
                setLoading(false);
                return;
            }

            if (form.isGift && !giftEmail) {
                setGiftEmailError("Debes ingresar el email del destinatario.");
                setLoading(false);
                return;
            }

            if (form.isGift && !GMAIL_REGEX.test(giftEmail)) {
                setGiftEmailError("Solo se admiten correos de Gmail (@gmail.com).");
                setLoading(false);
                return;
            }

            const payload = {
                openVoucherId: voucher.id,
                quantity: Number(form.vouchersQty) || 1, // número de bonos a comprar
                source: "openVoucher",
                ...(isValidCoupon && { couponId }),
                unitPrice: voucher.amount,
                totalPrice: finalTotal,
                buyer: {
                    name: form.name,
                    email: form.email,
                    phone: form.phone,
                },
                isGift: form.isGift,
                giftDetails: form.isGift
                    ? {
                        toName: form.giftName,
                        emailTo: form.giftEmail,
                        message: form.message,
                        from: form.name,
                    }
                    : undefined,
            };

            const res = await axios.post(`${process.env.REACT_APP_URI}/v1/payments/checkout`, payload);
            if (res.data?.url) {
                window.location.href = res.data.url;
            } else {
                throw new Error("No se pudo iniciar el pago con Stripe");
            }
        } catch (err) {
            console.error("Error al generar el bono", err);
            alert("No se pudo completar la compra del bono.");
        } finally {
            setLoading(false);
        }
    };

    const background = voucher?.imageUrl
        ? `linear-gradient(115deg, rgba(0,0,0,0.35), rgba(0,0,0,0.2)), url(${voucher.imageUrl})`
        : "linear-gradient(120deg, #f5f5f5, #e8e8e8)";

    const isBuyerEmailValid = GMAIL_REGEX.test(form.email.trim());
    const isGiftEmailValid = !form.isGift || (form.giftEmail && GMAIL_REGEX.test(form.giftEmail.trim()));

    const originalTotal = voucher && form.vouchersQty
        ? Number(voucher.amount || 0) * (Number(form.vouchersQty) || 1)
        : 0;

    const finalTotal = Math.max(0, originalTotal - discount);

    const isFormInvalid =
        loading ||
        !voucher ||
        !form.name ||
        !form.email ||
        !form.phone ||
        !form.vouchersQty ||
        Number(form.vouchersQty) < 1 ||
        !isBuyerEmailValid ||
        (form.isGift && (!form.giftName || !form.giftEmail || !isGiftEmailValid));

    const applyCoupon = () => {
        const found = coupons.find((c) => c.code === couponCode);
        if (!found) {
            setCouponError("Cupón inválido.");
            setIsValidCoupon(false);
            setDiscount(0);
            setCouponId("");
            return;
        }
        if (found.remainingUses <= 0) {
            setCouponError("El cupón ya fue usado el máximo de veces.");
            setIsValidCoupon(false);
            setDiscount(0);
            setCouponId("");
            return;
        }
        if (new Date(found.expiresAt) < new Date()) {
            setCouponError("El cupón ha expirado.");
            setIsValidCoupon(false);
            setDiscount(0);
            setCouponId("");
            return;
        }

        const basePrice = originalTotal;
        if (found.minSpend && basePrice < found.minSpend) {
            const faltante = (found.minSpend - basePrice).toFixed(2);
            setCouponError(`Necesitas gastar €${found.minSpend.toFixed(2)} (te faltan €${faltante}) para usar este cupón.`);
            setIsValidCoupon(false);
            setDiscount(0);
            setCouponId("");
            return;
        }

        setDiscount(Number(found.amount || 0));
        setIsValidCoupon(true);
        setCouponId(found.id);
        setCouponError("");
    };

    const cancelCoupon = () => {
        setCouponCode("");
        setDiscount(0);
        setIsValidCoupon(false);
        setCouponId("");
        setCouponError("");
    };

    return (
        <div className="position-relative">
            <Navbar />

            <section className="col-11 mx-auto my-5">
                {loading && <div className="text-center my-4">Cargando bono...</div>}
                {!loading && error && (
                    <div className="alert alert-warning" role="alert">
                        {error}
                    </div>
                )}

                <div className="row g-4">
                    <div className="col-12 col-lg-7">
                        <div className="card shadow border-0">
                            <div className="card-body p-4 p-lg-5">
                                <h3 className="mb-4">Tus datos (solo datos del comprador)</h3>
                                <form id="openVoucherForm" onSubmit={handleSubmit}>
                                    <div className="row g-3">
                                        <div className="col-md-6">
                                            <label className="form-label">Nombre y Apellido</label>
                                            <input
                                                name="name"
                                                type="text"
                                                className="form-control"
                                                value={form.name}
                                                onChange={handleChange}
                                                required
                                            />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label">Email</label>
                                            <input
                                                name="email"
                                                type="email"
                                                className="form-control"
                                                value={form.email}
                                                onChange={handleChange}
                                                required
                                            />
                                            {emailError && (
                                                <div className="text-danger small mt-1">{emailError}</div>
                                            )}
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label">Teléfono (sin prefijo de país)</label>
                                            <input
                                                name="phone"
                                                type="tel"
                                                className="form-control"
                                                value={form.phone}
                                                onChange={handleChange}
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="form-check form-switch mt-4">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id="isGift"
                                            name="isGift"
                                            checked={form.isGift}
                                            onChange={handleChange}
                                        />
                                        <label className="form-check-label" htmlFor="isGift">
                                            ¿Es un regalo?
                                        </label>
                                        {form.isGift && (
                                            <div className="text-muted small mt-1">
                                                Si no quieres que el bono regalo le llegue al destinario hoy, no marques la opción es un regalo ( solo rellena tus datos)                                            </div>
                                        )}
                                    </div>

                                    {form.isGift && (
                                        <div className="row g-3 mt-2">
                                            <div className="col-md-6">
                                                <label className="form-label">Nombre del destinatario</label>
                                                <input
                                                    name="giftName"
                                                    type="text"
                                                    className="form-control"
                                                    value={form.giftName}
                                                    onChange={handleChange}
                                                    required={form.isGift}
                                                />
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-label">Email del destinatario</label>
                                                <input
                                                    name="giftEmail"
                                                    type="email"
                                                    className="form-control"
                                                    value={form.giftEmail}
                                                    onChange={handleChange}
                                                    required={form.isGift}
                                                />
                                                {giftEmailError && (
                                                    <div className="text-danger small mt-1">{giftEmailError}</div>
                                                )}
                                            </div>
                                            <div className="col-12">
                                                <label className="form-label">Mensaje (opcional)</label>
                                                <textarea
                                                    name="message"
                                                    rows="3"
                                                    className="form-control"
                                                    value={form.message}
                                                    onChange={handleChange}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </form>
                            </div>
                        </div>
                    </div>

                    <div className="col-12 col-lg-5">
                        <div className="card shadow border-0">
                            <div className="card-body p-4">
                                <div className="d-flex align-items-center gap-3 mb-3">
                                    <div
                                        style={{
                                            width: "70px",
                                            height: "70px",
                                            borderRadius: "12px",
                                            backgroundSize: "cover",
                                            backgroundPosition: "center",
                                            backgroundImage: voucher?.imageUrl
                                                ? `url(${voucher.imageUrl})`
                                                : "linear-gradient(120deg, #f5f5f5, #e8e8e8)",
                                        }}
                                    ></div>
                                    <div>
                                        <h5 className="mb-1">{voucher?.name || "Bono regalo"}</h5>
                                        <small className="text-muted">
                                            {voucher?.description || "Bono para cualquier experiencia."}
                                        </small>
                                    </div>
                                </div>

                                <div className="mb-3">
                                    <label className="form-label text-muted">Código de cupón</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Código de cupón"
                                        value={couponCode}
                                        onChange={(e) => setCouponCode(e.target.value)}
                                        disabled={isValidCoupon}
                                    />
                                    {couponError && (
                                        <div className="text-danger small mt-1">{couponError}</div>
                                    )}
                                    {!isValidCoupon ? (
                                        <button
                                            type="button"
                                            className="btn btn-outline-primary w-100 mt-2"
                                            onClick={applyCoupon}
                                        >
                                            Aplicar cupón
                                        </button>
                                    ) : (
                                        <button
                                            type="button"
                                            className="btn btn-outline-danger w-100 mt-2"
                                            onClick={cancelCoupon}
                                        >
                                            Cancelar cupón
                                        </button>
                                    )}
                                </div>

                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <span className="text-muted">Subtotal</span>
                                    <strong>
                                        {originalTotal.toFixed(2)} {voucher?.currency || "EUR"}
                                    </strong>
                                </div>
                                {isValidCoupon && (
                                    <div className="d-flex justify-content-between align-items-center mb-2 text-success">
                                        <span className="text-muted">Cupón aplicado</span>
                                        <strong>-{discount.toFixed(2)} {voucher?.currency || "EUR"}</strong>
                                    </div>
                                )}
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <span className="text-muted">Total a pagar</span>
                                    <strong>
                                        {finalTotal.toFixed(2)} {voucher?.currency || "EUR"}
                                    </strong>
                                </div>

                                <div className="mb-3">
                                    <label className="form-label">Cantidad de bonos (cada bono es para 2 personas)</label>
                                    <select
                                        name="vouchersQty"
                                        className="form-select"
                                        value={form.vouchersQty}
                                        onChange={handleChange}
                                    >
                                        {[...Array(8)].map((_, i) => (
                                            <option key={i + 1} value={i + 1}>
                                                {i + 1} bono(s) de 2 personas (cada uno)
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <button
                                    type="submit"
                                    form="openVoucherForm"
                                    className="btn btn-primary w-100 mt-3 fw-bold"
                                    disabled={isFormInvalid}
                                >
                                    Continuar con el pago
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default OpenVoucherCheckout;

